/**
 * Centralized query key factory for React Query
 *
 * This ensures consistent query keys across the application and makes
 * it easier to invalidate or refetch related queries.
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys
 */
import { server } from '@/constants';

/**
 * Authentication query keys
 */
export const authQueryKeys = {
  all: ['auth'] as const,
  me: () => [...authQueryKeys.all, 'me'] as const,
};

/**
 * Organization query keys
 */
export const orgQueryKeys = {
  all: ['orgs'] as const,
  lists: () => [...orgQueryKeys.all, 'list'] as const,
  list: () => ['orgs'] as const,
  details: () => [...orgQueryKeys.all, 'detail'] as const,
  detail: (orgId: string) => [...orgQueryKeys.details(), orgId] as const,
  users: (orgId: string) => [`orgs/${orgId}/users`] as const,
  userSearch: (searchQuery: string) => ['users', 'search', searchQuery] as const,
  // Endpoint URL builders
  endpoints: {
    list: () => `${server}/orgs`,
    detail: (orgId: string) => `${server}/orgs/${orgId}`,
  },
};

/**
 * Study query keys and endpoint URLs
 */
export const studyQueryKeys = {
  all: ['studies'] as const,
  lists: () => ['studies', 'list'] as const,
  list: (orgId: string) => [`orgs/${orgId}/studies`] as const,
  listSearch: (orgId: string) => [`orgs/${orgId}/studies/search`] as const,
  details: () => ['studies', 'detail'] as const,
  detail: (orgId: string, studyId: string) => [`orgs/${orgId}/studies/${studyId}`] as const,
  users: (orgId: string, studyId: string) => [`orgs/${orgId}/studies/${studyId}`, 'users'] as const,
  search: (orgId: string, searchQuery: string) => ['studies', 'search', orgId, searchQuery] as const,
  // Not org-scoped: the backend widens the result set to every org the caller can reach.
  crossOrgSearch: () => ['studies', 'cross-org-search'] as const,
  permissions: (orgId: string, studyId: string) => [`orgs/${orgId}/studies/${studyId}`, 'permissions'] as const,
  instrumentRuns: (orgId: string, studyId: string) => [`orgs/${orgId}/studies/${studyId}/instrument_runs`] as const,
  // Replaces the removed sample_proteoform_counts search. Keyed by assay because the
  // endpoint requires assay_id — one results table per assay.
  proteoformSampleSummaries: (orgId: string, studyId: string, assayId: string) =>
    [`orgs/${orgId}/studies/${studyId}/proteoform_sample_summaries`, assayId] as const,
  proteoforms: (orgId: string, studyId: string) => [`orgs/${orgId}/studies/${studyId}/sample_proteoforms`] as const,
  studySamples: (orgId: string, studyId: string) => [`orgs/${orgId}/studies/${studyId}/samples`] as const,
  endpoints: {
    list: (orgId: string) => `${server}/orgs/${orgId}/studies`,
    listSearch: (orgId: string) => `${server}/orgs/${orgId}/studies/search`,
    detail: (orgId: string, studyId: string) => `${server}/orgs/${orgId}/studies/${studyId}`,
    users: (orgId: string, studyId: string) => `${server}/orgs/${orgId}/studies/${studyId}/users`,
    owners: (orgId: string, studyId: string) => `${server}/orgs/${orgId}/studies/${studyId}/owners`,
    proteoformSampleSummariesSearch: (orgId: string, studyId: string) =>
      `${server}/orgs/${orgId}/studies/${studyId}/proteoform_sample_summaries/search`,
    proteoformsSearch: (orgId: string, studyId: string) =>
      `${server}/orgs/${orgId}/studies/${studyId}/sample_proteoforms/search`,
    samplesSearch: (orgId: string, studyId: string) => `${server}/orgs/${orgId}/studies/${studyId}/samples/search`,
    samplesDownload: (orgId: string, studyId: string) => `${server}/orgs/${orgId}/studies/${studyId}/samples/download`,
    crossOrgSearch: () => `${server}/studies/search`,
    permissions: (orgId: string, studyId: string) => `${server}/orgs/${orgId}/studies/${studyId}/permissions`,
    usersBatch: (orgId: string, studyId: string) => `${server}/orgs/${orgId}/studies/${studyId}/users/batch`,
    instrumentRuns: (orgId: string, studyId: string) => `${server}/orgs/${orgId}/studies/${studyId}/instrument_runs`,
    instrumentRunsSearch: (orgId: string, studyId: string) =>
      `${server}/orgs/${orgId}/studies/${studyId}/instrument_runs/search`,
  },
};

/**
 * Assay catalog query keys (POR-322 backend BFF endpoints)
 */
export const assayQueryKeys = {
  all: ['assays'] as const,
  list: () => [...assayQueryKeys.all, 'list'] as const,
  runConfigs: () => [...assayQueryKeys.all, 'run_configs'] as const,
  proteoformResultsTableConfig: (assayId: string) =>
    [...assayQueryKeys.all, assayId, 'proteoform_results_table_config'] as const,
  detail: (assayId: string) => [...assayQueryKeys.all, assayId] as const,
  // Endpoint URL builders
  endpoints: {
    // No trailing slash: the backend route is now declared as `/assays`, and `/assays/`
    // 307-redirects to it. (This was the other way round previously — verified against a
    // locally-run backend on the r17 schema.) fetch follows the redirect either way, but the
    // slash form costs a pointless extra round trip.
    list: () => `${server}/assays`,
    runConfigs: () => `${server}/assays/run_configs`,
    proteoformResultsTableConfig: (assayId: string) => `${server}/assays/${assayId}/proteoform_results_table_config`,
    detail: (assayId: string) => `${server}/assays/${assayId}`,
  },
};

/**
 * Instrument query keys
 */
export const instrumentQueryKeys = {
  all: ['instruments'] as const,
  list: (orgId: string) => [`orgs/${orgId}/instruments`] as const,
  detail: (orgId: string, instrumentId: string) => [`orgs/${orgId}/instruments/${instrumentId}`] as const,
  // Endpoint URL builders
  endpoints: {
    list: (orgId: string) => `${server}/orgs/${orgId}/instruments`,
    detail: (orgId: string, instrumentId: string) => `${server}/orgs/${orgId}/instruments/${instrumentId}`,
  },
};

/**
 * Instrument run query keys
 */
export const instrumentRunQueryKeys = {
  all: ['instrument_runs'] as const,
  lists: () => [...instrumentRunQueryKeys.all, 'list'] as const,
  list: (orgId: string) => [`orgs/${orgId}/instrument_runs`] as const,
  details: () => [...instrumentRunQueryKeys.all, 'detail'] as const,
  detail: (orgId: string, runId: string) => [`orgs/${orgId}/instrument_runs/${runId}`] as const,
  // The endpoint now EXISTS (POST /orgs/{org_id}/instrument_runs/{id}/sample_proteoforms/search).
  // TODO: wire RunSampleDetailsSection's grid datasource to it — the key/URL are ready, only the
  // grid is still using the non-paginated path.
  proteoformsSearch: (orgId: string, runId: string) =>
    [`orgs/${orgId}/instrument_runs/${runId}/sample_proteoforms/search`] as const,
  draft: (orgId: string) => [`orgs/${orgId}/instrument_runs/draft`] as const,
  // Endpoint URL builders
  endpoints: {
    list: (orgId: string) => `${server}/orgs/${orgId}/instrument_runs`,
    detail: (orgId: string, runId: string) => `${server}/orgs/${orgId}/instrument_runs/${runId}`,
    /**
     * Draft endpoint for instrument runs (use for both POST and PUT draft operations)
     */
    draft: (orgId: string) => `${server}/orgs/${orgId}/instrument_runs/draft`,
    samplesDownload: (orgId: string, runId: string) =>
      `${server}/orgs/${orgId}/instrument_runs/${runId}/samples/download`,
    samplesUpload: (orgId: string) => `${server}/orgs/${orgId}/samples/upload`,
    /**
     * Run-scoped proteoform search. Shipped on the backend as of the r17 schema work; the
     * grid that should consume it has not been switched over yet.
     */
    proteoformsSearch: (orgId: string, runId: string) =>
      `${server}/orgs/${orgId}/instrument_runs/${runId}/sample_proteoforms/search`,
    /**
     * Submit instrument run (POST)
     */
    submit: (orgId: string, runId: string) => `${server}/orgs/${orgId}/instrument_runs/${runId}/submit`,
    /**
     * Status-independent PATCH for the 7 user-entered consumable lot/serial fields.
     */
    consumables: (orgId: string, runId: string) => `${server}/orgs/${orgId}/instrument_runs/${runId}/consumables`,
  },
};

/**
 * SAML authentication query keys
 */
export const samlQueryKeys = {
  all: ['saml'] as const,
  metadata: () => ['samlMetadata'] as const,
  provider: () => ['samlProvider'] as const,

  // Endpoint URL builders
  endpoints: {
    metadata: () => `${server}/auth/saml/metadata`,
    provider: () => `${server}/auth/saml/provider`,
  },
};

/**
 * Sample query keys
 */
export const sampleQueryKeys = {
  all: ['samples'] as const,
  list: (orgId: string) => [`orgs/${orgId}/samples`] as const,
  listSearch: (orgId: string) => [`orgs/${orgId}/samples/search`] as const,
  qcSummary: (orgId: string) => [`orgs/${orgId}/samples/qc_summary`] as const,
  detail: (orgId: string, sampleId: string) => [`orgs/${orgId}/samples/${sampleId}`] as const,
  proteoforms: (orgId: string, sampleId: string) => [`orgs/${orgId}/samples/${sampleId}/sample_proteoforms`] as const,
  proteoformsSearch: (orgId: string, sampleId: string) =>
    [`orgs/${orgId}/samples/${sampleId}/sample_proteoforms/search`] as const,
  // Endpoint URL builders
  endpoints: {
    list: (orgId: string) => `${server}/orgs/${orgId}/samples`,
    listSearch: (orgId: string) => `${server}/orgs/${orgId}/samples/search`,
    qcSummary: (orgId: string) => `${server}/orgs/${orgId}/samples/qc_summary`,
    detail: (orgId: string, sampleId: string) => `${server}/orgs/${orgId}/samples/${sampleId}`,
    proteoforms: (orgId: string, sampleId: string) => `${server}/orgs/${orgId}/samples/${sampleId}/sample_proteoforms`,
    proteoformsSearch: (orgId: string, sampleId: string) =>
      `${server}/orgs/${orgId}/samples/${sampleId}/sample_proteoforms/search`,
  },
};

/**
 * User query keys
 */
export const userQueryKeys = {
  all: ['users'] as const,
  orgUsers: (orgId: string) => ['org-users', orgId] as const,
  search: (searchQuery: string) => [...userQueryKeys.all, 'search', searchQuery] as const,
  globalSearch: () => [...userQueryKeys.all, 'global-search'] as const,
  details: () => [...userQueryKeys.all, 'detail'] as const,
  detail: (userId: string) => [...userQueryKeys.details(), userId] as const,
  studies: (userId: string) => [...userQueryKeys.detail(userId), 'studies'] as const,
  // Endpoint URL builders
  endpoints: {
    orgUsers: (orgId: string) => `${server}/orgs/${orgId}/users`,
    globalSearch: () => `${server}/users/search`,
    detail: (userId: string) => `${server}/users/${userId}`,
    studies: (userId: string) => `${server}/users/${userId}/studies`,
  },
};
